/**
 * ActivityLogPage — /#/activity-log
 *
 * Reverse-chronological activity feed across all roles.
 * Roles: All (managers/executives see all; reps see own rows)
 *
 * Data source: GET /api/activity/feed
 *   Reads from daily_activity_actual + cadence_step_completions.
 *   Filtered by tenant and user role server-side.
 *
 * 2026-05-20: Replaced 25-row hard-coded seed with live API fetch.
 *   Removed "Seed data" badge.
 */

import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";

const BLACK = "#0B0B0F";
const BG = "#FAFAF7";

// ── Types ─────────────────────────────────────────────────────────────────────

type FeedRowType = "daily_log" | "cadence_step";

interface FeedRow {
  id: string;
  ts: string;           // ISO
  actor: string;
  actorRole: string;
  type: FeedRowType;
  account: string;
  opp: string;
  description: string;
  source?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const TYPE_META: Record<FeedRowType, { label: string; bg: string; color: string }> = {
  daily_log:    { label: "Activity log",   bg: "#F0FDF4", color: "#15803D" },
  cadence_step: { label: "Cadence step",   bg: "#FAFAF7", color: "#0B0B0F" },
};

function TypeBadge({ type }: { type: FeedRowType }) {
  const m = TYPE_META[type] ?? { label: type, bg: "#EFEFEE", color: "#525258" };
  return (
    <span
      style={{
        background: m.bg, color: m.color, fontSize: 10, fontWeight: 700,
        letterSpacing: "0.08em", textTransform: "uppercase", padding: "2px 7px", borderRadius: 99,
        whiteSpace: "nowrap",
      }}
    >
      {m.label}
    </span>
  );
}

function fmtTs(iso: string): string {
  const dt = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - dt.getTime();
  const diffH = diffMs / 3_600_000;
  if (diffH < 1) return `${Math.round(diffMs / 60000)}m ago`;
  if (diffH < 24) return `${Math.round(diffH)}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return "Yesterday " + dt.toLocaleTimeString("en-US", { timeZone: "America/Detroit", hour: "2-digit", minute: "2-digit" });
  return `${diffD}d ago · ` + dt.toLocaleTimeString("en-US", { timeZone: "America/Detroit", hour: "2-digit", minute: "2-digit" });
}

const PAGE_SIZE = 50;

// ── Main ─────────────────────────────────────────────────────────────────────

export default function ActivityLogPage() {
  const [, navigate] = useLocation();
  const [feed, setFeed]           = useState<FeedRow[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<FeedRowType | "all">("all");
  const [page, setPage]           = useState(1);

  // Fetch from server on mount
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch("/api/activity/feed?limit=200", { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json: { feed: FeedRow[]; total: number }) => {
        if (cancelled) return;
        setFeed(json.feed ?? []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load activity");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    return feed.filter((a) => {
      if (typeFilter !== "all" && a.type !== typeFilter) return false;
      return true;
    });
  }, [feed, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const ALL_TYPES: Array<FeedRowType | "all"> = ["all", "daily_log", "cadence_step"];

  return (
    <OperatorShell breadcrumbs={[{ label: "Activity Log" }]}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: BLACK, margin: 0, letterSpacing: "-0.01em" }}>Activity Log</h1>
        </div>
        <div style={{ fontSize: 14, color: "#6B6B70", marginBottom: 24 }}>
          Cross-team activity feed — daily logs and cadence step completions.
        </div>

        {/* Filters */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20, alignItems: "flex-end" }}>
          <div>
            <label style={{ display: "block", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8E8E94", marginBottom: 4 }}>Type</label>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value as FeedRowType | "all"); setPage(1); }}
              style={{ padding: "7px 10px", borderRadius: 8, border: "1px solid #E5E5E7", fontSize: 12, color: BLACK, background: "#FFFFFF" }}
            >
              <option value="all">All types</option>
              <option value="daily_log">Activity log</option>
              <option value="cadence_step">Cadence step</option>
            </select>
          </div>
          <div style={{ fontSize: 12, color: "#8E8E94", paddingBottom: 6 }}>
            {loading ? "Loading…" : `${filtered.length} activit${filtered.length !== 1 ? "ies" : "y"}`}
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div style={{ padding: "16px 20px", borderRadius: 8, background: "#FEF2F2", border: "1px solid #FCA5A5", color: "#991B1B", fontSize: 13, marginBottom: 20 }}>
            Could not load activity feed: {error}
          </div>
        )}

        {/* Loading state */}
        {loading && !error && (
          <div style={{ padding: "40px 16px", textAlign: "center", fontSize: 13, color: "#8E8E94" }}>
            Loading activity…
          </div>
        )}

        {/* Empty state (loaded but no rows) */}
        {!loading && !error && feed.length === 0 && (
          <div style={{ padding: "40px 16px", textAlign: "center", fontSize: 13, color: "#8E8E94" }}>
            No activity logged yet. Activity appears here once reps log their first daily entries.
          </div>
        )}

        {/* Table */}
        {!loading && !error && feed.length > 0 && (
          <div style={{ background: "#FFFFFF", border: "1px solid #E5E5E7", borderRadius: 12, overflow: "hidden" }}>
            {/* Header */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "130px 140px 120px 180px 1fr",
                gap: 12,
                padding: "10px 16px",
                borderBottom: "1px solid #E5E5E7",
                background: "#FAFAF7",
              }}
            >
              {["Time", "Actor", "Type", "Account", "Description"].map((h) => (
                <div key={h} style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: "#8E8E94" }}>{h}</div>
              ))}
            </div>

            {/* Rows */}
            {pageRows.length === 0 ? (
              <div style={{ padding: "40px 16px", textAlign: "center", fontSize: 13, color: "#8E8E94" }}>No activities match those filters.</div>
            ) : (
              pageRows.map((a) => (
                <div
                  key={a.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "130px 140px 120px 180px 1fr",
                    gap: 12,
                    padding: "12px 16px",
                    borderBottom: "1px solid #EFEFEE",
                    alignItems: "start",
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "#FAFAF7"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  <div style={{ fontSize: 11, color: "#8E8E94", fontVariantNumeric: "tabular-nums" }}>{fmtTs(a.ts)}</div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: BLACK }}>{a.actor || "—"}</div>
                    {a.actorRole && <div style={{ fontSize: 10, color: "#8E8E94", textTransform: "uppercase", letterSpacing: "0.06em" }}>{a.actorRole}</div>}
                  </div>
                  <TypeBadge type={a.type} />
                  <div>
                    {a.account ? (
                      <div style={{ fontSize: 12, fontWeight: 600, color: BLACK }}>{a.account}</div>
                    ) : (
                      <span style={{ fontSize: 12, color: "#8E8E94" }}>—</span>
                    )}
                    {a.opp && <div style={{ fontSize: 10, color: "#6B6B70", marginTop: 1 }}>{a.opp}</div>}
                  </div>
                  <div style={{ fontSize: 12, color: "#525258", lineHeight: 1.5 }}>{a.description}</div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && !loading && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 20 }}>
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              style={{
                padding: "6px 14px", borderRadius: 8, border: "1px solid #E5E5E7",
                background: page === 1 ? "#EFEFEE" : "#FFFFFF", color: page === 1 ? "#8E8E94" : BLACK,
                fontSize: 12, fontWeight: 600, cursor: page === 1 ? "default" : "pointer",
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: 12, color: "#6B6B70" }}>Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              style={{
                padding: "6px 14px", borderRadius: 8, border: "1px solid #E5E5E7",
                background: page === totalPages ? "#EFEFEE" : "#FFFFFF", color: page === totalPages ? "#8E8E94" : BLACK,
                fontSize: 12, fontWeight: 600, cursor: page === totalPages ? "default" : "pointer",
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </OperatorShell>
  );
}
